import type { Container } from 'pixi.js';
import type { Stage } from './render/stage';
import type { DogId } from './assets/names';
import type { LevelDef } from './core/level';
import { LEVELS, getLevel } from './levels';
import type { Nav } from './meta/nav';
import { loadSave, resetSave, storeSave } from './meta/save';
import { isUnlocked, newSave, recordWin, useBooster, type ProgressEvent, type SaveData, type WinResult } from './meta/progress';
import { GameplayScreen, type GameplayHost } from './screens/GameplayScreen';
import { CafeScreen } from './screens/CafeScreen';
import { WorldMapScreen } from './screens/WorldMapScreen';
import { DogCollectionScreen } from './screens/DogCollectionScreen';
import { DogArrivalScreen } from './screens/DogArrivalScreen';
import { LocationUnlockScreen } from './screens/LocationUnlockScreen';
import { openModal } from './ui/modal';
import { Commerce } from './monetization/commerce';
import { testPurchaseSheet, testRewardedVideo } from './ui/testOverlays';
import { ShopScreen } from './screens/ShopScreen';
import { buyWithFeedback } from './ui/offers';
import { AD_REWARDS, continueCost } from './monetization/products';
import type { Layout } from './render/stage';
import { button, label, toast } from './ui/components';
import { FONT } from './config/tokens';
import { settings, updateSettings } from './settings';
import { haptic } from './audio/haptics';

/**
 * Screen flow and save game:
 *   first start → level 1 (tutorial) → win → café (dirty Honey, "Fix up the café")
 *   → buy → "Honey found a home!" → café → play … → level 18 → "Welcome to Paris!" → map
 */
export class App implements Nav {
  private data: SaveData;
  readonly commerce: Commerce;

  constructor(private readonly stage: Stage, readonly debug: boolean) {
    this.data = loadSave();
    this.commerce = new Commerce(() => this.data, (s) => this.update(s));
    this.commerce.setTestUi(
      (p, price) => testPurchaseSheet(this.stage.top, this.stage.layout, p, price),
      () => testRewardedVideo(this.stage.top, this.stage.layout),
    );
    void this.commerce.init();
  }

  layout(): Layout | undefined {
    return this.stage.layout;
  }

  /** URL options: ?reset=1 new game, ?level=N play a level, ?debug=1 everything open + unlimited boosters. */
  start(params: URLSearchParams, custom?: LevelDef): void {
    if (params.has('reset')) {
      resetSave();
      this.data = newSave();
    }
    if (custom) return this.play(custom, { custom: true });
    const direct = Number(params.get('level'));
    if (direct && getLevel(direct)) return this.playLevel(direct);
    if (this.data.nextLevel === 1) return this.playLevel(1);
    this.showCafe();
  }

  save(): SaveData {
    return this.data;
  }

  update(s: SaveData): void {
    this.data = s;
    storeSave(s);
  }

  playLevel(id: number): void {
    const level = getLevel(Math.min(id, LEVELS.length));
    if (level) this.play(level, { custom: false });
  }

  private play(level: LevelDef, opts: { custom: boolean }): void {
    const unlimited = opts.custom || this.debug;
    const host: GameplayHost = {
      tutorial: level.id === 1 && !opts.custom && this.data.nextLevel === 1,
      boosters: () => (unlimited ? null : this.data.boosters),
      useBooster: (b) => {
        if (unlimited) return true;
        const s = structuredClone(this.data);
        const ok = useBooster(s, b);
        if (ok) this.update(s);
        return ok;
      },
      coins: () => this.data.coins,
      spendCoins: (n) => {
        if (this.data.coins < n) return false;
        this.update({ ...this.data, coins: this.data.coins - n });
        return true;
      },
      won: (stars) => {
        if (opts.custom) return null;
        const r = recordWin(this.data, level.id, stars);
        this.update(r.save);
        return r;
      },
      continueAfterWin: (r) => this.afterWin(level, r),
      quit: () => (opts.custom ? this.play(level, opts) : isUnlocked(this.data, 'cafe') ? this.showCafe() : this.playLevel(level.id)),
      canQuitToCafe: !opts.custom && isUnlocked(this.data, 'cafe'),
      continueCost,
      videoMoves: AD_REWARDS.continueMoves,
      watchAd: async () => (await this.commerce.watchAd()) === 'rewarded',
      buyCoins: (parent) => this.quickCoins(parent),
    };
    this.stage.show(new GameplayScreen(level, host));
  }

  private afterWin(level: LevelDef, r: WinResult | null): void {
    if (!r) return this.play(level, { custom: true });
    if (r.events.some((e) => e.type === 'unlock' && e.what === 'paris')) {
      this.stage.show(new LocationUnlockScreen(() => this.showMap()));
      return;
    }
    this.showCafe(r.events);
  }

  showCafe(notes: ProgressEvent[] = []): void {
    // rewarded videos (consent + tracking prompt) start once the shop is open, never on the very first launch
    if (isUnlocked(this.data, 'shop')) void this.commerce.startAds();
    this.stage.show(new CafeScreen(this, notes));
  }

  showShop(): void {
    this.stage.show(new ShopScreen(this));
  }

  /** Out of moves without enough coins: small coin offer, right inside the level. */
  private quickCoins(parent: Container): Promise<boolean> {
    return new Promise((resolve) => {
      const w = 720;
      const m = openModal(parent, this.stage.layout, w, 620);
      const t = label('Need a few more coins?', FONT.size.screenTitle - 4, { weight: FONT.weight.heading });
      t.position.set(w / 2, 90);
      m.panel.addChild(t);
      const finish = (ok: boolean) => {
        m.close();
        resolve(ok);
      };
      (['coins_small', 'coins_medium'] as const).forEach((id, i) => {
        const price = this.commerce.price(id)?.priceString;
        const amount = id === 'coins_small' ? '500' : '1,200';
        const b = button(price ? `${amount} Coins · ${price}` : `${amount} Coins`, 560, 'primary', () => {
          if (!price || this.commerce.isBusy) return;
          void buyWithFeedback(this, id, parent, (this.stage.layout?.safeTop ?? 92) + 200).then((ok) => ok && finish(true));
        }, 96);
        b.position.set(w / 2, 220 + i * 130);
        m.panel.addChild(b);
      });
      const no = button('No, thanks', 400, 'secondary', () => finish(false), 84);
      no.position.set(w / 2, 510);
      m.panel.addChild(no);
    });
  }

  showMap(): void {
    this.stage.show(new WorldMapScreen(this));
  }

  showDogs(): void {
    this.stage.show(new DogCollectionScreen(this));
  }

  showArrival(dog: DogId): void {
    this.stage.show(new DogArrivalScreen(dog, () => this.showCafe()));
  }

  showSettings(parent: Container): void {
    const rows: [string, () => void][] = [
      [`Sound: ${settings().sound ? 'On' : 'Off'}`, () => (updateSettings({ sound: !settings().sound }), m.close(), this.showSettings(parent))],
      [`Vibration: ${settings().haptics ? 'On' : 'Off'}`, () => (updateSettings({ haptics: !settings().haptics }), haptic('medium'), m.close(), this.showSettings(parent))],
      ['Restore purchases', () => void this.restoreFrom(parent, m.close)],
      ['Level editor', () => (window.location.href = './editor.html')],
      ['Asset gallery', () => (window.location.href = './gallery.html')],
      ['Start over', () => this.confirmReset(parent, m.close)],
      ['Close', () => m.close()],
    ];
    const w = 640;
    const m = openModal(parent, this.stage.layout, w, 170 + rows.length * 108);
    const title = label('Settings', FONT.size.screenTitle, { weight: FONT.weight.heading });
    title.position.set(w / 2, 80);
    m.panel.addChild(title);
    rows.forEach(([text, onTap], i) => {
      const b = button(text, 480, i === rows.length - 1 ? 'primary' : 'secondary', onTap, 86);
      b.position.set(w / 2, 175 + i * 108);
      m.panel.addChild(b);
    });
  }

  private async restoreFrom(parent: Container, closeSettings: () => void): Promise<void> {
    closeSettings();
    const restored = await this.commerce.restore();
    const y = (this.stage.layout?.safeTop ?? 92) + 200;
    toast(parent, restored === null ? 'Could not reach the store.' : restored.length ? 'Your Starter Pack is back!' : 'Everything is already restored.', y);
  }

  private confirmReset(parent: Container, closeSettings: () => void): void {
    closeSettings();
    const w = 700;
    const m = openModal(parent, this.stage.layout, w, 480);
    const t = label('Start over?\nAll progress will be lost.', FONT.size.body + 4, { weight: FONT.weight.heading });
    t.style.align = 'center';
    t.position.set(w / 2, 120);
    const yes = button('Start over', 460, 'secondary', () => {
      m.close();
      resetSave();
      this.data = newSave();
      this.playLevel(1);
    }, 88);
    yes.position.set(w / 2, 270);
    const no = button('Keep playing', 460, 'primary', () => m.close(), 88);
    no.position.set(w / 2, 380);
    m.panel.addChild(t, yes, no);
  }
}
